/* ==============================================================
   AMIGO PROVISIONS — Cart interactivity
   --------------------------------------------------------------
   <amigo-cart> custom element wraps the cart form. Qty +/- buttons
   and Remove links update the cart via Shopify's /cart/change.js
   AJAX endpoint, then re-render the affected line totals + the
   summary (subtotal, shipping, total, free-ship progress bar)
   without a page reload.

   Falls back to a full-page redirect to /cart if fetch fails.
   ============================================================== */
(() => {
  class AmigoCart extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.freeShipThreshold = parseInt(this.dataset.freeShipThreshold, 10) || 0;
      this.shippingFlatCents = parseInt(this.dataset.shippingFlatCents, 10) || 0;

      this.subtotalEl = this.querySelector('[data-cart-subtotal]');
      this.shippingEl = this.querySelector('[data-cart-shipping]');
      this.totalEl = this.querySelector('[data-cart-total]');
      this.checkoutBtn = this.querySelector('[data-cart-checkout]');
      this.progressBarEl = this.querySelector('[data-cart-bar-fill]');
      this.progressTextEl = this.querySelector('[data-cart-progress-text]');
      this.linesContainer = this.querySelector('[data-cart-lines]');
      this.countEl = this.querySelector('[data-cart-count]');

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
    }

    handleClick(e) {
      const step = e.target.closest('[data-qty-step]');
      if (step) {
        e.preventDefault();
        this.stepQty(step);
        return;
      }
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.removeLine(remove);
        return;
      }
    }

    handleChange(e) {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      const next = Math.max(0, parseInt(input.value, 10) || 0);
      this.updateLine(input.dataset.lineKey, next, input.closest('[data-cart-line]'));
    }

    stepQty(btn) {
      const lineEl = btn.closest('[data-cart-line]');
      if (!lineEl) return;
      const inputs = lineEl.querySelectorAll('[data-qty-input]');
      const delta = parseInt(btn.dataset.qtyStep, 10);
      inputs.forEach(input => {
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        input.value = next;
      });
      const firstInput = inputs[0];
      this.updateLine(firstInput.dataset.lineKey, parseInt(firstInput.value, 10), lineEl);
    }

    removeLine(btn) {
      const lineEl = btn.closest('[data-cart-line]');
      if (!lineEl) return;
      const key = lineEl.dataset.lineKey;
      this.updateLine(key, 0, lineEl);
    }

    async updateLine(key, quantity, lineEl) {
      if (!key) return;
      lineEl.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error('cart change failed: ' + res.status);
        const cart = await res.json();
        this.applyCart(cart, key, quantity, lineEl);
      } catch (err) {
        console.error('[amigo-cart] update failed, falling back to reload', err);
        window.location.href = '/cart';
      } finally {
        lineEl.removeAttribute('aria-busy');
      }
    }

    applyCart(cart, changedKey, changedQty, lineEl) {
      // Update or remove the changed line
      const updatedItem = cart.items.find(i => i.key === changedKey);
      if (!updatedItem || changedQty === 0) {
        lineEl.remove();
      } else {
        // Sync the qty inputs (could have been adjusted by server)
        lineEl.querySelectorAll('[data-qty-input]').forEach(input => {
          input.value = updatedItem.quantity;
        });
        // Update the line-side desktop price + mobile inline price
        const linePrice = this.formatMoney(updatedItem.final_line_price);
        lineEl.querySelectorAll('[data-cart-lineprice]').forEach(el => {
          el.textContent = linePrice;
        });
      }

      // Update summary
      if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.items_subtotal_price);
      if (this.countEl) {
        const n = cart.item_count;
        this.countEl.textContent = n + ' ' + (n === 1 ? 'item' : 'items');
      }

      // Recompute shipping + total based on threshold
      const sub = cart.items_subtotal_price;
      const meetsThreshold = this.freeShipThreshold > 0 && sub >= this.freeShipThreshold;
      const shipCents = meetsThreshold ? 0 : this.shippingFlatCents;
      const total = sub + shipCents;
      if (this.shippingEl) {
        this.shippingEl.textContent = meetsThreshold
          ? 'Free'
          : (this.shippingFlatCents > 0 ? this.formatMoney(shipCents) : 'Calculated at checkout');
      }
      if (this.totalEl) {
        const totalDisplay = (this.shippingFlatCents > 0 || meetsThreshold) ? total : sub;
        this.totalEl.textContent = this.formatMoney(totalDisplay);
      }
      if (this.checkoutBtn) {
        const lbl = this.checkoutBtn.querySelector('[data-checkout-label]');
        if (lbl) lbl.textContent = 'Checkout · ' + this.formatMoney(sub);
        this.checkoutBtn.disabled = cart.item_count === 0;
      }

      // Free-ship progress bar
      if (this.freeShipThreshold > 0 && this.progressBarEl) {
        const pct = Math.min(100, (sub / this.freeShipThreshold) * 100);
        this.progressBarEl.style.width = pct + '%';
        if (this.progressTextEl) {
          if (sub >= this.freeShipThreshold) {
            this.progressTextEl.innerHTML = "<strong>You've unlocked free shipping.</strong> Nice work.";
          } else {
            const remaining = this.formatMoney(this.freeShipThreshold - sub);
            this.progressTextEl.innerHTML =
              "You're <strong>" + remaining + "</strong> away from <strong>free shipping</strong>.";
          }
        }
      }

      // If cart is empty, reload to show empty state (simpler than rendering inline)
      if (cart.item_count === 0) {
        window.location.href = '/cart';
      }
    }

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return this.moneyFormat
        .replace(/{{\s*amount\s*}}/, dollars)
        .replace(/{{\s*amount_no_decimals\s*}}/, Math.round(cents / 100).toString())
        .replace(/{{\s*amount_with_comma_separator\s*}}/, dollars.replace(/\./, ','));
    }
  }

  if (!customElements.get('amigo-cart')) {
    customElements.define('amigo-cart', AmigoCart);
  }
})();
